import { z } from 'zod';
import { cuid, dinheiro, paginacao, taxa } from './primitives.js';
import { percentualParaTaxa } from './itens-tributos.js';

export const CENTRO_TIPOS = ['APOIO', 'PRODUTIVO'] as const;
export type CentroTipo = (typeof CENTRO_TIPOS)[number];
export const ROTULO_CENTRO_TIPO: Record<CentroTipo, string> = { APOIO: 'Apoio', PRODUTIVO: 'Produtivo' };

/** Tipo do no na arvore (XARP `type`: synthetic/analytic). SINTETICO so agrupa; ANALITICO recebe CPM, minutos e etapas. */
export const CENTRO_TIPOS_NO = ['SINTETICO', 'ANALITICO'] as const;
export type CentroTipoNo = (typeof CENTRO_TIPOS_NO)[number];
export const ROTULO_CENTRO_TIPO_NO: Record<CentroTipoNo, string> = { SINTETICO: 'Sintético', ANALITICO: 'Analítico' };

/** XARP `nature`: administrative/productive. */
export const CENTRO_NATUREZAS = ['ADMINISTRATIVO', 'PRODUTIVO'] as const;
export type CentroNatureza = (typeof CENTRO_NATUREZAS)[number];
export const ROTULO_CENTRO_NATUREZA: Record<CentroNatureza, string> = { ADMINISTRATIVO: 'Administrativo', PRODUTIVO: 'Produtivo' };

/** XARP `classification`: processing/support (so analitico produtivo). */
export const CENTRO_CLASSIFICACOES = ['TRANSFORMACAO', 'APOIO'] as const;
export type CentroClassificacao = (typeof CENTRO_CLASSIFICACOES)[number];
export const ROTULO_CENTRO_CLASSIFICACAO: Record<CentroClassificacao, string> = { TRANSFORMACAO: 'Transformação', APOIO: 'Apoio' };

/** XARP `workCenterType`: HUMAN/MACHINE/HYBRID (so analitico produtivo de transformacao). */
export const CENTRO_TRABALHO_TIPOS = ['HOMEM', 'MAQUINA', 'MISTO'] as const;
export type CentroTrabalhoTipo = (typeof CENTRO_TRABALHO_TIPOS)[number];
export const ROTULO_CENTRO_TRABALHO: Record<CentroTrabalhoTipo, string> = { HOMEM: 'Homem', MAQUINA: 'Máquina', MISTO: 'Misto' };

/** Origem do custo por minuto de um mes: digitado, importado do XARP ou apurado por rateio. */
export const CPM_ORIGENS = ['MANUAL', 'IMPORTADO_XARP', 'RATEIO'] as const;
export type CpmOrigem = (typeof CPM_ORIGENS)[number];
export const ROTULO_CPM_ORIGEM: Record<CpmOrigem, string> = { MANUAL: 'manual', IMPORTADO_XARP: 'importado do XARP', RATEIO: 'rateio' };

/** Custo por minuto: R$/min como string decimal com ate 10 casas (a unica excecao a escala de dinheiro, ver schema). */
export const cpmValor = z.string().regex(/^\d{1,8}(\.\d{1,10})?$/, 'CPM invalido (ate 10 casas, sem sinal, use ponto)');
/** Competencia "AAAA-MM" (o primeiro dia do mes e implicito). */
export const competencia = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Competencia invalida (use AAAA-MM)');

const codigo = z.string().trim().min(1, 'Informe o código').max(40, 'O código tem no máximo 40 caracteres');
const nome = z.string().trim().min(1, 'Informe o nome do centro').max(200, 'O nome tem no máximo 200 caracteres');

const identificador = z.string().trim().min(1, 'Informe o identificador').max(40, 'O identificador tem no máximo 40 caracteres');
const areaM2 = dinheiro.nullable();

/**
 * Criar centro. Novo contrato: `identificador` (segmento local, unico entre irmaos), `parentId`, `tipoNo`, `natureza`,
 * `classificacao`, `tipoTrabalho`, `areaM2`. Legado (tela atual): `codigo` + `tipo` (APOIO/PRODUTIVO) criam um ANALITICO na raiz.
 * `codigo` (unico por empresa, imutavel) e opcional: padrao = caminho. Informe `identificador` ou `codigo`.
 */
export const criarCentroSchema = z
  .object({
    identificador: identificador.optional(),
    codigo: codigo.optional(),
    nome,
    /** LEGADO: APOIO/PRODUTIVO. Ignorado quando `natureza` vem. */
    tipo: z.enum(CENTRO_TIPOS).optional(),
    parentId: cuid.nullish(),
    tipoNo: z.enum(CENTRO_TIPOS_NO).optional(),
    natureza: z.enum(CENTRO_NATUREZAS).nullish(),
    classificacao: z.enum(CENTRO_CLASSIFICACOES).nullish(),
    tipoTrabalho: z.enum(CENTRO_TRABALHO_TIPOS).nullish(),
    areaM2: areaM2.optional(),
    /** Minutos disponiveis por mes (sem R$); null/ausente = nao informado. So ANALITICO. */
    minutosDisponiveisMes: dinheiro.nullish(),
    ativo: z.boolean().default(true),
  })
  .refine((v) => v.identificador !== undefined || v.codigo !== undefined, { message: 'Informe o identificador', path: ['identificador'] });
export type CriarCentroDto = z.infer<typeof criarCentroSchema>;

/** O codigo e imutavel. Mudar o pai e `PATCH :id/mover`. Desativar sintetico desativa a subarvore. */
export const atualizarCentroSchema = z
  .object({
    nome,
    identificador,
    tipo: z.enum(CENTRO_TIPOS),
    tipoNo: z.enum(CENTRO_TIPOS_NO),
    natureza: z.enum(CENTRO_NATUREZAS).nullable(),
    classificacao: z.enum(CENTRO_CLASSIFICACOES).nullable(),
    tipoTrabalho: z.enum(CENTRO_TRABALHO_TIPOS).nullable(),
    areaM2,
    minutosDisponiveisMes: dinheiro.nullable(),
    /** Sessao 9: ociosidade do centro (fracao, 0 <= x < 1); null = usa a da empresa. */
    ociosidade: taxa.refine((v) => Number(v) < 1, 'Ociosidade menor que 100%').nullable(),
    ativo: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCentroDto = z.infer<typeof atualizarCentroSchema>;

export const listarCentrosSchema = paginacao.extend({
  ativo: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  q: z.string().trim().min(1).max(100).optional(),
  tipoNo: z.enum(CENTRO_TIPOS_NO).optional(),
  parentId: cuid.optional(),
});
export type ListarCentrosQuery = z.infer<typeof listarCentrosSchema>;

/** PATCH /centros-custo/:id/mover: novo pai (sintetico) ou null = raiz. */
export const moverCentroSchema = z.object({ parentId: cuid.nullable() });
export type MoverCentroDto = z.infer<typeof moverCentroSchema>;

/** GET /centros-custo/arvore e /analiticos. */
export const consultaArvoreSchema = z.object({
  incluirInativos: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ConsultaArvoreQuery = z.infer<typeof consultaArvoreSchema>;

/** Formulario do cadastro (RHF): minutos como texto (vazio = nao informado). */
export const centroFormSchema = z.object({
  codigo,
  nome,
  tipo: z.enum(CENTRO_TIPOS),
  minutosDisponiveisMes: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\d{1,12}([.,]\d{1,6})?$/.test(v), 'Minutos invalidos (numero, ate 6 casas)'),
  ativo: z.boolean(),
});
export type CentroFormValores = z.infer<typeof centroFormSchema>;

/** GET /centros-custo/cpm: janela de `meses` competencias terminando em `ate` (padrao: o mes corrente). */
export const consultaCpmSchema = z.object({
  ate: competencia.optional(),
  meses: z.coerce.number().int().min(1).max(36).default(13),
  incluirInativos: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ConsultaCpmQuery = z.infer<typeof consultaCpmSchema>;

/** Uma alteracao da grade: grava a PROXIMA versao do mes (a anterior fica no historico). */
export const alteracaoCpmSchema = z.object({
  centroId: cuid,
  competencia,
  valor: cpmValor,
  /** MANUAL (digitado) ou RATEIO (apurado fora); IMPORTADO_XARP so a importacao grava. */
  origem: z.enum(['MANUAL', 'RATEIO']).default('MANUAL'),
  minutosDisponiveis: dinheiro.nullish(),
  custoTotal: dinheiro.nullish(),
  motivo: z.string().trim().min(1).max(300).optional(),
});
export type AlteracaoCpmDto = z.infer<typeof alteracaoCpmSchema>;

export const gravarCpmSchema = z.object({ alteracoes: z.array(alteracaoCpmSchema).min(1).max(500) });
export type GravarCpmDto = z.infer<typeof gravarCpmSchema>;
export type GravarCpmInput = z.input<typeof gravarCpmSchema>;

export interface CentroCustoResponse {
  id: string;
  codigo: string;
  nome: string;
  tipo: CentroTipo;
  minutosDisponiveisMes: string | null;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Centro com os campos da arvore (todas as rotas de centros devolvem este formato; a base e a do contrato legado). */
export interface CentroNoResponse extends CentroCustoResponse {
  parentId: string | null;
  identificador: string;
  /** Identificadores dos ancestrais + o proprio, separados por ponto ("3.1.2"). Derivado. */
  caminho: string;
  /** Profundidade: 0 = raiz. */
  nivel: number;
  tipoNo: CentroTipoNo;
  natureza: CentroNatureza | null;
  classificacao: CentroClassificacao | null;
  tipoTrabalho: CentroTrabalhoTipo | null;
  areaM2: string | null;
  /** Sessao 9: ociosidade do centro (fracao); null/ausente = usa a da empresa. */
  ociosidade?: string | null;
}

export interface CentroArvoreNo extends CentroNoResponse {
  filhos: CentroArvoreNo[];
}

/** Para ficha e grade CPM: so ANALITICOS, identificados pelo caminho. */
export interface CentroAnaliticoResponse {
  id: string;
  codigo: string;
  identificador: string;
  nome: string;
  caminho: string;
  /** "caminho · nome", pronto para combos. */
  rotulo: string;
  natureza: CentroNatureza | null;
  classificacao: CentroClassificacao | null;
  tipoTrabalho: CentroTrabalhoTipo | null;
  minutosDisponiveisMes: string | null;
  ativo: boolean;
}

/** Numero digitado no formulario da arvore: texto pt-BR ou com ponto; '' = nao informado. Ate 6 casas. */
const decimalDigitadoForm = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{1,12}([.,]\d{1,6})?$/.test(v), 'Número inválido (até 6 casas)');

/** Ociosidade digitada em percentual ("5", "2,5"); '' = usa a da empresa. Aceita 0 a menos de 100%. */
const ociosidadeDigitadaForm = z
  .string()
  .trim()
  .refine((v) => v === '' || (percentualParaTaxa(v) !== null && percentualParaTaxa(v) !== '1'), 'Ociosidade deve ser de 0% a menos de 100% (até 6 casas)');

/**
 * Formulario da arvore (RHF): selects usam '' para "nao informado" (vira null ao enviar). As regras do XARP que o formulario
 * espelha (a API e quem impoe): sintetico so agrupa (sem minutos nem area); classificacao so em analitico produtivo;
 * tipo de trabalho obrigatorio em todo analitico (a API recusa com TIPO_TRABALHO_OBRIGATORIO). Tipo (sintetico x analitico) muda so se a API deixar.
 */
export const centroArvoreFormSchema = z
  .object({
    identificador,
    nome,
    tipoNo: z.enum(CENTRO_TIPOS_NO),
    natureza: z.union([z.literal(''), z.enum(CENTRO_NATUREZAS)]),
    classificacao: z.union([z.literal(''), z.enum(CENTRO_CLASSIFICACOES)]),
    tipoTrabalho: z.union([z.literal(''), z.enum(CENTRO_TRABALHO_TIPOS)]),
    areaM2: decimalDigitadoForm,
    minutosDisponiveisMes: decimalDigitadoForm,
    ociosidade: ociosidadeDigitadaForm,
    ativo: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.tipoNo === 'ANALITICO' && v.tipoTrabalho === '') ctx.addIssue({ code: 'custom', path: ['tipoTrabalho'], message: 'Informe o tipo de centro de trabalho' });
    if (v.tipoNo !== 'SINTETICO') return;
    if (v.minutosDisponiveisMes !== '') ctx.addIssue({ code: 'custom', path: ['minutosDisponiveisMes'], message: 'Centro sintético só agrupa: não tem minutos disponíveis' });
    if (v.areaM2 !== '') ctx.addIssue({ code: 'custom', path: ['areaM2'], message: 'Centro sintético não tem área' });
  });
export type CentroArvoreFormValores = z.infer<typeof centroArvoreFormSchema>;

/** GET :id/uso e motivos do POST :id/excluir. */
export interface CentroUsoResponse {
  filhos: number;
  cpm: number;
  etapasFicha: number;
  lotes: number;
  importacao: number;
  /** true quando nada impede a exclusao definitiva. */
  podeExcluir: boolean;
}

export interface ExcluirCentroResponse {
  resultado: 'EXCLUIDO' | 'DESATIVADO';
  /** Quando DESATIVADO: o que impediu a exclusao. */
  motivos: Array<'FILHOS' | 'CPM' | 'ETAPAS_FICHA' | 'LOTES' | 'IMPORTACAO'>;
  /** Centros desativados (inclui descendentes). */
  desativados: number;
}

/** Um mes de um centro, na versao vigente. `valor` e `custoTotal` (R$) so com `cpm.ver`. */
export interface CpmCelulaResponse {
  competencia: string;
  versao: number;
  /** R$/min com 10 casas. So com cpm.ver. */
  valor?: string;
  /** So com cpm.ver. */
  custoTotal?: string | null;
  minutosDisponiveis: string | null;
  origem: CpmOrigem;
  motivo: string | null;
  criadoPor: string | null;
  criadoEm: string;
}

/** Linha da grade: so ANALITICOS. Os campos da arvore vem sempre da API; opcionais no tipo para nao quebrar quem monta a linha. */
export interface CentroGradeResponse extends CentroCustoResponse, Partial<Omit<CentroNoResponse, keyof CentroCustoResponse>> {
  /** competencia -> versao vigente. Ausente (a chave nao existe) para quem nao tem `cpm.ver` (PCP). */
  celulas?: Record<string, CpmCelulaResponse>;
}

export interface CpmGradeResponse {
  /** Competencias da janela, da mais antiga para a mais nova. */
  meses: string[];
  centros: CentroGradeResponse[];
  /** Ultima competencia com CPM salvo em qualquer centro (fora da janela tambem). So com cpm.ver. */
  ultimoMesSalvo?: string | null;
}

export interface GravarCpmResponse {
  gravadas: number;
  /** Alteracoes iguais ao que ja valia (nada novo foi gravado). */
  semMudanca: number;
  celulas: Array<CpmCelulaResponse & { centroId: string }>;
  /** Lote (ja APLICADO, modo AUTOMATICO) que registrou o impacto desta gravacao. */
  loteId?: string;
}

/**
 * Centro que aceita ETAPA de ficha (processo produtivo): ANALITICO, ativo, produtivo e de TRANSFORMACAO. Sintetico (so agrupa), de apoio e
 * administrativo ficam de fora. Cadastro antigo produtivo sem classificacao conta como transformacao (S24-5b). A API recusa (422
 * `CENTRO_NAO_PRODUTIVO`) e a tela so oferece estes.
 */
export function centroAceitaEtapa(c: { tipoNo: CentroTipoNo; natureza: CentroNatureza | null; classificacao: CentroClassificacao | null; ativo: boolean }): boolean {
  return c.ativo && c.tipoNo === 'ANALITICO' && c.natureza === 'PRODUTIVO' && c.classificacao !== 'APOIO';
}
